import { useState, useMemo, Fragment } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { 
  ArrowLeft, 
  Search, 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  Check, 
  X, 
  MessageCircle, 
  Eye,
  ArrowUpDown,
  Calendar,
  Building,
  AlertCircle,
  CheckCircle,
  Clock,
  User
} from "lucide-react";

export interface DemandItem {
  id: string;
  title: string;
  description: string;
  status: "New" | "Under Review" | "Approved" | "Rejected" | "On Hold";
  priority: "Critical" | "High" | "Medium" | "Low";
  department: string;
  requestedBy: string;
  submittedDate: string;
  estimatedCost: number;
  businessCase: string;
  comments: Array<{
    id: string;
    author: string;
    content: string;
    timestamp: string;
  }>;
  activityHistory: Array<{
    id: string;
    action: string;
    actor: string;
    timestamp: string;
    details?: string;
  }>;
}

// Mock data
const mockDemands: DemandItem[] = [
  {
    id: "DEM-001",
    title: "Customer Portal Enhancement",
    description: "Upgrade the customer portal with new self-service features and improved user interface",
    status: "New",
    priority: "High",
    department: "IT",
    requestedBy: "Sarah Johnson",
    submittedDate: "2024-12-15",
    estimatedCost: 75000,
    businessCase: "Improve customer satisfaction and reduce support tickets by 30%",
    comments: [
      {
        id: "c1",
        author: "Mike Davis",
        content: "This looks promising. We should prioritize the mobile-first approach.",
        timestamp: "2024-12-16T10:30:00Z"
      }
    ],
    activityHistory: [
      {
        id: "a1",
        action: "Submitted",
        actor: "Sarah Johnson",
        timestamp: "2024-12-15T14:20:00Z"
      },
      {
        id: "a2",
        action: "Assigned for Review",
        actor: "System",
        timestamp: "2024-12-15T14:21:00Z",
        details: "Assigned to Mike Davis"
      }
    ]
  },
  {
    id: "DEM-002",
    title: "HR Management System Update",
    description: "Modernize the HR system with cloud-based solution and automated workflows",
    status: "Under Review",
    priority: "Medium",
    department: "HR",
    requestedBy: "Robert Chen",
    submittedDate: "2024-12-10",
    estimatedCost: 120000,
    businessCase: "Streamline HR processes and improve employee experience",
    comments: [
      {
        id: "c2",
        author: "Lisa Wang",
        content: "Cost estimates seem reasonable. Need more details on implementation timeline.",
        timestamp: "2024-12-12T09:15:00Z"
      },
      {
        id: "c3",
        author: "Robert Chen",
        content: "Timeline will be 6-8 months including training and migration.",
        timestamp: "2024-12-12T11:45:00Z"
      }
    ],
    activityHistory: [
      {
        id: "a3",
        action: "Submitted",
        actor: "Robert Chen",
        timestamp: "2024-12-10T16:30:00Z"
      },
      {
        id: "a4",
        action: "Review Started",
        actor: "Lisa Wang",
        timestamp: "2024-12-11T08:00:00Z"
      }
    ]
  },
  {
    id: "DEM-003",
    title: "Marketing Automation Platform",
    description: "Implement comprehensive marketing automation to improve lead nurturing and conversion",
    status: "Approved",
    priority: "High",
    department: "Marketing",
    requestedBy: "Emma Thompson",
    submittedDate: "2024-12-05",
    estimatedCost: 95000,
    businessCase: "Increase lead conversion rate by 25% and reduce manual marketing tasks",
    comments: [
      {
        id: "c4",
        author: "John Smith",
        content: "Approved for Q1 implementation. Great ROI projections.",
        timestamp: "2024-12-08T14:20:00Z"
      }
    ],
    activityHistory: [
      {
        id: "a5",
        action: "Submitted",
        actor: "Emma Thompson",
        timestamp: "2024-12-05T10:15:00Z"
      },
      {
        id: "a6",
        action: "Approved",
        actor: "John Smith",
        timestamp: "2024-12-08T14:20:00Z",
        details: "Approved for Q1 2025 implementation"
      }
    ]
  },
  {
    id: "DEM-004",
    title: "Office Space Renovation",
    description: "Renovate the main office space to support hybrid work model",
    status: "Rejected",
    priority: "Low",
    department: "Operations",
    requestedBy: "David Wilson",
    submittedDate: "2024-11-28",
    estimatedCost: 200000,
    businessCase: "Improve employee satisfaction and collaboration in hybrid work environment",
    comments: [
      {
        id: "c5",
        author: "Finance Team",
        content: "Budget constraints for this fiscal year. Consider resubmitting for next year.",
        timestamp: "2024-12-01T16:45:00Z"
      }
    ],
    activityHistory: [
      {
        id: "a7",
        action: "Submitted",
        actor: "David Wilson",
        timestamp: "2024-11-28T12:00:00Z"
      },
      {
        id: "a8",
        action: "Rejected",
        actor: "Finance Team",
        timestamp: "2024-12-01T16:45:00Z",
        details: "Budget constraints for FY2024"
      }
    ]
  },
  {
    id: "DEM-005",
    title: "Security Infrastructure Upgrade",
    description: "Upgrade network security infrastructure with next-gen firewalls and monitoring",
    status: "On Hold",
    priority: "Critical",
    department: "IT",
    requestedBy: "Alex Rodriguez",
    submittedDate: "2024-12-01",
    estimatedCost: 150000,
    businessCase: "Address critical security vulnerabilities and ensure compliance",
    comments: [
      {
        id: "c6",
        author: "Security Team",
        content: "Waiting for vendor proposal clarifications before proceeding.",
        timestamp: "2024-12-03T11:30:00Z"
      }
    ],
    activityHistory: [
      {
        id: "a9",
        action: "Submitted",
        actor: "Alex Rodriguez",
        timestamp: "2024-12-01T09:20:00Z"
      },
      {
        id: "a10",
        action: "Put On Hold",
        actor: "Security Team",
        timestamp: "2024-12-03T11:30:00Z",
        details: "Awaiting vendor clarifications"
      }
    ]
  }
];

type SortField = "title" | "status" | "priority" | "department" | "submittedDate" | "estimatedCost";
type SortDirection = "asc" | "desc";

interface DemandBacklogInterfaceProps {
  onNavigateBack: () => void;
}

export function DemandBacklogInterface({ onNavigateBack }: DemandBacklogInterfaceProps) {
  const [demands] = useState<DemandItem[]>(mockDemands);
  const [selectedDemands, setSelectedDemands] = useState<Set<string>>(new Set());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>("submittedDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Filtering and sorting logic
  const filteredAndSortedDemands = useMemo(() => {
    let filtered = demands.filter(demand => {
      const matchesSearch = demand.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          demand.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          demand.requestedBy.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || demand.status === statusFilter;
      const matchesPriority = priorityFilter === "all" || demand.priority === priorityFilter;
      const matchesDepartment = departmentFilter === "all" || demand.department === departmentFilter;

      return matchesSearch && matchesStatus && matchesPriority && matchesDepartment;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === "submittedDate") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (sortField === "estimatedCost") {
        aValue = Number(aValue);
        bValue = Number(bValue);
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [demands, searchTerm, statusFilter, priorityFilter, departmentFilter, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const toggleSelectDemand = (demandId: string) => {
    const newSelection = new Set(selectedDemands);
    if (newSelection.has(demandId)) {
      newSelection.delete(demandId);
    } else {
      newSelection.add(demandId);
    }
    setSelectedDemands(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedDemands.size === filteredAndSortedDemands.length) {
      setSelectedDemands(new Set());
    } else {
      setSelectedDemands(new Set(filteredAndSortedDemands.map(d => d.id)));
    }
  };

  const toggleExpandRow = (demandId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(demandId)) {
      newExpanded.delete(demandId);
    } else {
      newExpanded.add(demandId);
    }
    setExpandedRows(newExpanded);
  };

  const handleBatchAction = (action: string) => {
    console.log(`Performing ${action} on demands:`, Array.from(selectedDemands));
    // Here you would implement the actual batch action logic
    setSelectedDemands(new Set());
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "New": return <Clock className="h-4 w-4" />;
      case "Under Review": return <Eye className="h-4 w-4" />;
      case "Approved": return <CheckCircle className="h-4 w-4" />;
      case "Rejected": return <X className="h-4 w-4" />;
      case "On Hold": return <AlertCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "New": return "bg-blue-100 text-blue-800 border-blue-200";
      case "Under Review": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Approved": return "bg-green-100 text-green-800 border-green-200";
      case "Rejected": return "bg-red-100 text-red-800 border-red-200";
      case "On Hold": return "bg-gray-100 text-gray-800 border-gray-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPriorityVariant = (priority: string) => {
    switch (priority) {
      case "Critical": 
        return "destructive";
      case "High": 
        return "outline";
      case "Medium": 
        return "secondary";
      case "Low": 
        return "outline";
      default: 
        return "outline";
    }
  };

  const getPriorityCustomClass = (priority: string) => {
    switch (priority) {
      case "Critical": 
        return ""; // Use default destructive styling
      case "High": 
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "Medium": 
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Low": 
        return "bg-green-100 text-green-800 border-green-200";
      default: 
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const activeFiltersCount = [statusFilter, priorityFilter, departmentFilter].filter(f => f !== "all").length;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={onNavigateBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl">Demand Backlog Management</h1>
              <p className="text-muted-foreground">
                Review, approve, and manage all submitted demands
              </p>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters & Search
              {activeFiltersCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {activeFiltersCount} active
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search demands..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="New">New</SelectItem>
                    <SelectItem value="Under Review">Under Review</SelectItem>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                    <SelectItem value="On Hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm">Priority</label>
                <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm">Department</label>
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    <SelectItem value="IT">IT</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="Marketing">Marketing</SelectItem>
                    <SelectItem value="Operations">Operations</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Batch Actions */}
        {selectedDemands.size > 0 && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="font-medium">
                    {selectedDemands.size} demand{selectedDemands.size !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        Bulk Approve
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Bulk Approval</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to approve {selectedDemands.size} selected demand{selectedDemands.size !== 1 ? 's' : ''}? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleBatchAction('approve')}>
                          Approve All
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="flex items-center gap-2">
                        <X className="h-4 w-4" />
                        Bulk Reject
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Bulk Rejection</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to reject {selectedDemands.size} selected demand{selectedDemands.size !== 1 ? 's' : ''}? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleBatchAction('reject')}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Reject All
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedDemands(new Set())}
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results Summary */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {filteredAndSortedDemands.length} of {demands.length} demands
          </p>
        </div>

        {/* Demands Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="text-left p-4 w-12">
                      <Checkbox
                        checked={selectedDemands.size === filteredAndSortedDemands.length && filteredAndSortedDemands.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left p-4 w-12"></th>
                    <th className="text-left p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort("title")}
                        className="h-auto p-0 font-medium"
                      >
                        Title
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </th>
                    <th className="text-left p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort("status")}
                        className="h-auto p-0 font-medium"
                      >
                        Status
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </th>
                    <th className="text-left p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort("priority")}
                        className="h-auto p-0 font-medium"
                      >
                        Priority
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </th>
                    <th className="text-left p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort("department")}
                        className="h-auto p-0 font-medium"
                      >
                        Department
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </th>
                    <th className="text-left p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort("submittedDate")}
                        className="h-auto p-0 font-medium"
                      >
                        Submitted
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </th>
                    <th className="text-left p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSort("estimatedCost")}
                        className="h-auto p-0 font-medium"
                      >
                        Cost
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </th>
                    <th className="text-left p-4 w-32">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedDemands.map((demand) => (
                    <Fragment key={demand.id}>
                      <tr className="border-b hover:bg-muted/25">
                        <td className="p-4">
                          <Checkbox
                            checked={selectedDemands.has(demand.id)}
                            onCheckedChange={() => toggleSelectDemand(demand.id)}
                          />
                        </td>
                        <td className="p-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpandRow(demand.id)}
                            className="h-auto p-0"
                          >
                            {expandedRows.has(demand.id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="font-medium">{demand.title}</p>
                            <p className="text-sm text-muted-foreground truncate max-w-xs">
                              {demand.description}
                            </p>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline" className={`${getStatusColor(demand.status)} flex items-center gap-1 w-fit`}>
                            {getStatusIcon(demand.status)}
                            {demand.status}
                          </Badge>
                        </td>
                        <td className="p-4">
                          {demand.priority === "Critical" ? (
                            <Badge variant="destructive" className="w-fit">
                              {demand.priority}
                            </Badge>
                          ) : (
                            <Badge 
                              variant={getPriorityVariant(demand.priority)} 
                              className={`${getPriorityCustomClass(demand.priority)} w-fit`}
                            >
                              {demand.priority}
                            </Badge>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-muted-foreground" />
                            {demand.department}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            {new Date(demand.submittedDate).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="font-medium">
                            ${demand.estimatedCost.toLocaleString()}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" className="flex items-center gap-1">
                              <Check className="h-3 w-3" />
                              Review
                            </Button>
                            <Button size="sm" variant="outline" className="flex items-center gap-1">
                              <MessageCircle className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Row Content */}
                      {expandedRows.has(demand.id) && (
                        <tr className="bg-muted/25">
                          <td colSpan={9} className="p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {/* Details */}
                              <div className="space-y-4">
                                <h4 className="font-medium">Demand Details</h4>
                                <div className="space-y-3">
                                  <div>
                                    <label className="text-sm text-muted-foreground">Business Case</label>
                                    <p className="text-sm">{demand.businessCase}</p>
                                  </div>
                                  <div>
                                    <label className="text-sm text-muted-foreground">Requested By</label>
                                    <div className="flex items-center gap-2">
                                      <User className="h-4 w-4 text-muted-foreground" />
                                      <span className="text-sm">{demand.requestedBy}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Comments */}
                                <div className="space-y-3">
                                  <h5 className="font-medium">Comments ({demand.comments.length})</h5>
                                  <div className="space-y-2 max-h-32 overflow-y-auto">
                                    {demand.comments.map((comment) => (
                                      <div key={comment.id} className="border rounded p-3 bg-background">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-sm font-medium">{comment.author}</span>
                                          <span className="text-xs text-muted-foreground">
                                            {new Date(comment.timestamp).toLocaleDateString()}
                                          </span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">{comment.content}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Activity History */}
                              <div className="space-y-4">
                                <h4 className="font-medium">Activity History</h4>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                  {demand.activityHistory.map((activity) => (
                                    <div key={activity.id} className="flex items-start gap-3 p-2 border rounded bg-background">
                                      <div className="w-2 h-2 rounded-full bg-primary mt-2"></div>
                                      <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                          <span className="text-sm font-medium">{activity.action}</span>
                                          <span className="text-xs text-muted-foreground">
                                            {new Date(activity.timestamp).toLocaleDateString()}
                                          </span>
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                          by {activity.actor}
                                          {activity.details && ` - ${activity.details}`}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
              
              {filteredAndSortedDemands.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No demands found matching your criteria.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}